import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { SERVICE_STATUS, SERVICE_TYPES, normalizeServiceType, CLP, API_BASE } from "@/lib/api";
import { PageContainer, PageHeader, StatusBadge, PrimaryButton, SecondaryButton, Field, inputCls } from "@/components/ui-kit";
import { ArrowLeft, Trash2, FileText, Save, Camera, ClipboardList, Plus, X } from "lucide-react";
import { toast } from "sonner";
import ReportModal from "@/components/ReportModal";
import RepairAutocomplete from "@/components/RepairAutocomplete";
import useTechnicians from "@/hooks/useTechnicians";
import EmailComposeModal from "@/components/EmailComposeModal";
import InvoiceForm from "@/components/InvoiceForm";
import FinanceEntryForm from "@/components/FinanceEntryForm";
import ServiceMargin from "@/components/ServiceMargin";

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

export default function ServiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [showCost, setShowCost] = useState(false);
  const [marginRefresh, setMarginRefresh] = useState(0);
  const { items: technicians } = useTechnicians();
  const prevStatusRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/services/${id}`);
    // Backfill repair_items from legacy text if not present
    if ((!data.repair_items || data.repair_items.length === 0) && data.repairs_needed) {
      data.repair_items = data.repairs_needed
        .split(/\n+/).filter(Boolean)
        .map((line) => ({ description: line.trim(), quantity: 1, unit_price: 0 }));
    }
    if (!data.repair_items) data.repair_items = [];
    if (data.repairs_tax_rate == null) data.repairs_tax_rate = 19;
    prevStatusRef.current = data.status;
    setSvc(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!svc) {
    return (
      <PageContainer>
        <div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div>
      </PageContainer>
    );
  }

  const save = async () => {
    const payload = {
      status: svc.status,
      observations: svc.observations,
      repair_items: (svc.repair_items || []).filter((i) => i.description?.trim()).map((i) => ({
        description: i.description,
        quantity: Number(i.quantity || 1),
        unit_price: Number(i.unit_price || 0),
      })),
      repairs_tax_rate: Number(svc.repairs_tax_rate || 19),
      repairs_needed: "",  // clear legacy
      technician: svc.technician,
      scheduled_date: svc.scheduled_date,
      duration_minutes: svc.duration_minutes,
      photos: svc.photos,
    };
    await api.patch(`/services/${id}`, payload);
    toast.success("Servicio actualizado");
    // Si el servicio se acaba de marcar como completado, ofrece registrar la facturación de inmediato.
    const wasCompleted = prevStatusRef.current === "completed";
    if (!wasCompleted && svc.status === "completed") {
      setShowInvoice(true);
    }
    load();
  };

  const remove = async () => {
    if (!window.confirm(svc.submission_id ? "¿Archivar esta visita? Su registro y envíos se conservarán en la ficha del cliente." : "¿Eliminar este servicio?")) return;
    const { data } = await api.delete(`/services/${id}`);
    toast.success(data.archived ? "Visita archivada · Historial conservado" : "Servicio eliminado");
    navigate("/admin/calendario");
  };

  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = await Promise.all(files.map(async (f) => ({ data: await toBase64(f), caption: f.name })));
    setSvc((s) => ({ ...s, photos: [...(s.photos || []), ...newPhotos] }));
  };

  const updateItem = (idx, key, value) => {
    const items = [...(svc.repair_items || [])];
    items[idx] = { ...items[idx], [key]: value };
    setSvc({ ...svc, repair_items: items });
  };

  const addItem = () => {
    setSvc({ ...svc, repair_items: [...(svc.repair_items || []), { description: "", quantity: 1, unit_price: 0 }] });
  };

  const removeItem = (idx) => {
    setSvc({ ...svc, repair_items: (svc.repair_items || []).filter((_, i) => i !== idx) });
  };

  const generateQuote = async () => {
    const items = (svc.repair_items || []).filter((i) => i.description?.trim());
    if (items.length === 0) {
      toast.error("Agrega al menos una reparación detectada antes de cotizar.");
      return;
    }
    const { data } = await api.post("/quotes", {
      customer_id: svc.customer_id,
      service_id: id,
      title: `Cotización por servicio ${svc.scheduled_date} — ${svc.customer?.name || ""}`,
      items: items.map((i) => ({ description: i.description, quantity: Number(i.quantity || 1), unit_price: Number(i.unit_price || 0) })),
      tax_rate: Number(svc.repairs_tax_rate || 19),
      status: "draft",
    });
    toast.success("Cotización creada");
    navigate(`/admin/cotizaciones/${data.id}`);
  };

  const subtotal = (svc.repair_items || []).reduce((a, it) => a + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
  const taxRate = Number(svc.repairs_tax_rate || 19);
  const tax = subtotal * (taxRate / 100);
  const total = subtotal + tax;

  return (
    <PageContainer>
      <Link to="/admin/calendario" data-testid="service-back" className="inline-flex items-center gap-1.5 text-xs font-mono-kpi uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FF5A00] mb-4">
        <ArrowLeft className="w-3 h-3" /> Volver al calendario
      </Link>

      <PageHeader
        kicker={`Servicio · ${svc.scheduled_date}`}
        title={svc.customer?.name || "Servicio"}
        subtitle={svc.customer?.address}
        actions={
          <div className="flex gap-2 flex-wrap">
            <Link data-testid="service-field-review" to={`/admin/revision-visitas/${id}`} className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-xs font-semibold hover:border-black"><ClipboardList size={15} />Registro técnico y revisión</Link>
            <SecondaryButton data-testid="service-show-report" onClick={() => setShowReport(true)}>
              <ClipboardList className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Generar informe
            </SecondaryButton>
            <SecondaryButton data-testid="service-send-email" onClick={() => setShowEmail(true)}>
              <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Enviar por correo
            </SecondaryButton>
            <SecondaryButton data-testid="service-generate-quote" onClick={generateQuote}>
              <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Generar cotización
            </SecondaryButton>
            <PrimaryButton data-testid="service-save" onClick={save}>
              <Save className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Guardar
            </PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-neutral-200 p-6">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] mb-4">// Informe del servicio</div>
            <Field label="Observaciones">
              <textarea data-testid="service-observations-edit" rows={5} value={svc.observations || ""} onChange={(e) => setSvc({ ...svc, observations: e.target.value })} className={inputCls} />
            </Field>
          </div>

          {/* Repair items with prices */}
          <div className="bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Reparaciones detectadas</div>
              <div className="flex items-center gap-2">
                <span className="font-mono-kpi text-[10px] text-neutral-500 uppercase tracking-wider">IVA (%)</span>
                <input
                  type="number" step="0.1"
                  data-testid="service-tax-rate-input"
                  value={svc.repairs_tax_rate}
                  onChange={(e) => setSvc({ ...svc, repairs_tax_rate: e.target.value })}
                  className={`${inputCls} w-20 text-right`}
                />
              </div>
            </div>
            <div className="border border-neutral-200">
              <div className="grid grid-cols-[1fr_80px_130px_130px_40px] gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                <div>Descripción</div>
                <div className="text-center">Cantidad</div>
                <div className="text-right">Precio Neto</div>
                <div className="text-right">Total Neto</div>
                <div />
              </div>
              {(svc.repair_items || []).length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-neutral-500">Sin reparaciones detectadas aún</div>
              )}
              {(svc.repair_items || []).map((it, i) => (
                <div key={`ri-${i}-${it.description || "draft"}`} data-testid={`service-repair-${i}`} className="grid grid-cols-[1fr_80px_130px_130px_40px] gap-2 px-3 py-2 border-b border-neutral-100 items-center">
                  <RepairAutocomplete
                    value={it.description || ""}
                    customerId={svc.customer_id}
                    onChange={(v) => updateItem(i, "description", v)}
                    onPickPrice={(price) => updateItem(i, "unit_price", price)}
                    className={inputCls}
                    placeholder="Ej: Cambio rodamientos elíptica"
                    testid={`service-repair-desc-${i}`}
                  />
                  <input
                    type="number" min="0" step="0.1"
                    data-testid={`service-repair-qty-${i}`}
                    value={it.quantity}
                    onChange={(e) => updateItem(i, "quantity", e.target.value)}
                    className={`${inputCls} text-center`}
                  />
                  <input
                    type="number" min="0"
                    data-testid={`service-repair-price-${i}`}
                    value={it.unit_price}
                    onChange={(e) => updateItem(i, "unit_price", e.target.value)}
                    className={`${inputCls} text-right`}
                  />
                  <div className="font-mono-kpi text-right text-sm">{CLP(Number(it.quantity || 0) * Number(it.unit_price || 0))}</div>
                  <button onClick={() => removeItem(i)} className="hover:bg-neutral-100 p-1.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button
                data-testid="service-add-repair"
                onClick={addItem}
                className="w-full text-xs font-mono-kpi uppercase tracking-[0.15em] text-[#FF5A00] hover:bg-[#FF5A00]/5 py-2 border-t border-neutral-100"
              >
                <Plus className="w-3 h-3 inline mr-1" /> Agregar reparación
              </button>
            </div>

            {/* Totals */}
            <div className="mt-4 bg-neutral-50 border border-neutral-200 p-4 grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="font-mono-kpi text-[9px] uppercase tracking-[0.2em] text-neutral-500">Subtotal Neto</div>
                <div className="font-mono-kpi font-bold mt-0.5">{CLP(subtotal)}</div>
              </div>
              <div>
                <div className="font-mono-kpi text-[9px] uppercase tracking-[0.2em] text-neutral-500">IVA ({taxRate}%)</div>
                <div className="font-mono-kpi font-bold mt-0.5">{CLP(tax)}</div>
              </div>
              <div>
                <div className="font-mono-kpi text-[9px] uppercase tracking-[0.2em] text-neutral-500">Total Bruto</div>
                <div className="font-mono-kpi font-bold text-[#FF5A00] text-lg mt-0.5" data-testid="service-repairs-total">{CLP(total)}</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Fotos del informe</div>
              <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-mono-kpi uppercase tracking-[0.15em] border border-neutral-300 px-3 py-2 hover:border-[#FF5A00]">
                <Camera className="w-3.5 h-3.5" />
                Agregar
                <input type="file" multiple accept="image/*" onChange={addPhotos} className="hidden" data-testid="service-photos-add" />
              </label>
            </div>
            {(!svc.photos || svc.photos.length === 0) ? (
              <div className="text-sm text-neutral-500 text-center py-8 border border-dashed border-neutral-300">Sin fotos</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {svc.photos.map((p, i) => (
                  <div key={p.data || `photo-${i}`} className="relative group">
                    <img src={p.data} alt={p.caption} className="w-full aspect-square object-cover border border-neutral-200" />
                    <button
                      onClick={() => setSvc({ ...svc, photos: svc.photos.filter((_, idx) => idx !== i) })}
                      className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-white border border-neutral-200 p-5">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-3">Detalles</div>
            <div className="space-y-3">
              <Field label="Estado">
                <select data-testid="service-status-edit" value={svc.status} onChange={(e) => setSvc({ ...svc, status: e.target.value })} className={inputCls}>
                  {SERVICE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
              <Field label="Fecha">
                <input type="date" value={svc.scheduled_date} onChange={(e) => setSvc({ ...svc, scheduled_date: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Técnico">
                {(() => {
                  const known = technicians.some((t) => t.name === svc.technician);
                  const otherMode = svc._technicianOther || (!!svc.technician && !known);
                  const selectValue = otherMode ? "__other__" : (known ? svc.technician : "");
                  return (
                    <>
                      <select data-testid="service-technician-edit"
                        value={selectValue}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "__other__") setSvc({ ...svc, technician: "", _technicianOther: true });
                          else setSvc({ ...svc, technician: v, _technicianOther: false });
                        }}
                        className={inputCls}>
                        <option value="">— Sin asignar —</option>
                        {technicians.map((t) => (
                          <option key={t.id} value={t.name}>{t.name}{t.role === "admin" ? " (admin)" : ""}</option>
                        ))}
                        <option value="__other__">Otro (escribir nombre)…</option>
                      </select>
                      {otherMode && (
                        <input
                          type="text" autoFocus
                          value={svc.technician || ""}
                          onChange={(e) => setSvc({ ...svc, technician: e.target.value })}
                          placeholder="Nombre del técnico externo"
                          data-testid="service-technician-other-edit"
                          className={`${inputCls} mt-2`}
                        />
                      )}
                    </>
                  );
                })()}
              </Field>
              <Field label="Duración (min)">
                <input type="number" value={svc.duration_minutes || 60} onChange={(e) => setSvc({ ...svc, duration_minutes: Number(e.target.value) })} className={inputCls} />
              </Field>
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-100">
              <StatusBadge status={svc.status} />
            </div>
          </div>

          {svc.customer && (
            <Link to={`/admin/clientes/${svc.customer.id}`} className="block bg-white border border-neutral-200 hover:border-[#0A0A0A] p-5">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Cliente</div>
              <div className="font-display font-bold text-base mt-1">{svc.customer.name}</div>
              <div className="text-xs text-neutral-500 mt-1">{svc.customer.address}</div>
              {svc.customer.requires_oc && (
                <div className="mt-2 inline-flex items-center gap-1 bg-[#FFC107]/15 text-[#B28C00] border border-[#FFC107]/40 px-2 py-0.5 text-[10px] font-mono-kpi uppercase">Con Orden de Compra</div>
              )}
            </Link>
          )}

          <ServiceMargin serviceId={id} refreshKey={marginRefresh} onRegisterCost={() => setShowCost(true)} />

          <button onClick={remove} data-testid="service-delete" className="w-full text-xs font-mono-kpi uppercase tracking-[0.15em] text-[#D50000] hover:bg-[#D50000]/5 border border-[#D50000]/30 px-3 py-2.5">
            <Trash2 className="w-3 h-3 inline mr-1.5" /> Eliminar servicio
          </button>
        </aside>
      </div>
      {showReport && <ReportModal serviceId={id} customerName={svc.customer?.name || "Servicio"} onClose={() => setShowReport(false)} />}
      {showEmail && (() => {
        const hasQuotable = (svc.repair_items || []).some((i) => Number(i.unit_price || 0) > 0);
        return (
          <EmailComposeModal
            onClose={() => setShowEmail(false)}
            serviceId={id}
            quoteId={svc.warranty_quote_id || null}
            customerId={svc.customer_id}
            hasQuotableRepairs={hasQuotable}
            defaultTemplate={(svc.warranty_quote_id || hasQuotable) ? "report_and_quote" : "report"}
            allowedTemplates={["report", "quote", "report_and_quote", "custom"]}
          />
        );
      })()}
      {showInvoice && (() => {
        const nType = normalizeServiceType(svc.service_type);
        const typeLabel = SERVICE_TYPES.find((t) => t.value === nType)?.label || "Servicio";
        const source = nType === "reparacion" ? "repair"
          : (nType === "mantencion_recurrente" || nType === "mantencion_unica") ? "maintenance"
          : "other";
        return (
          <InvoiceForm
            defaultCustomerId={svc.customer_id}
            defaultConcept={`${typeLabel} — ${svc.scheduled_date}`}
            defaultNetAmount={(svc.repair_items || []).reduce((a, i) => a + Number(i.quantity || 0) * Number(i.unit_price || 0), 0)}
            defaultSource={source}
            defaultSourceId={id}
            onClose={() => setShowInvoice(false)}
            onSaved={() => { setShowInvoice(false); toast.success("Facturación registrada en Finanzas"); setMarginRefresh((n) => n + 1); }}
          />
        );
      })()}
      {showCost && (
        <FinanceEntryForm
          defaultCustomerId={svc.customer_id}
          defaultServiceId={id}
          defaultTechnician={svc.technician}
          defaultCategory={svc.customer?.name}
          defaultDescription={`Costo del servicio ${svc.scheduled_date}`}
          onClose={() => setShowCost(false)}
          onCreated={() => { setShowCost(false); toast.success("Costo registrado"); setMarginRefresh((n) => n + 1); }}
        />
      )}
    </PageContainer>
  );
}
